import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AsyncStatus } from "../../components/types";
import NewTableComponent from "src/core/components/table/new-table.component";
import { GetConfigButton } from "../../components/get-config-button.component";
import type { TimerProfile } from "../../components/types";
import { getTimerProfiles } from "src/core/api/services/command-module/timer-profile.service";

export type TableDataRef = {
  refetch: () => Promise<void>;
  getProfiles: () => TimerProfile[];
};

type TimerProfileTableProps = {
  assetCode: string | null;
  selectedProfileId: string | null;
  commandStatus: AsyncStatus;
  commandCountdown: number;
  isLoadingCommandResult: boolean;
  onSelectProfile: (profile: TimerProfile) => void;
  onGetProfileConfig: () => void;
  onViewCommandResult: () => void;
  onAddProfile: () => void;
  onTableDataReady?: (data: TableDataRef) => void;
};

const tableHeaders = [
  {
    title: "Profile ID",
    label: "profileId",
    isSorted: true,
  },
  {
    title: "Profile Name",
    label: "profileName",
    isSorted: true,
  },
  {
    title: "Status",
    label: "status",
    isSorted: true,
    renderProps: (profile: TimerProfile) =>(
      <span
        className={`profile-status ${
          profile.status === "Enabled" ? "profile-status--enabled" : profile.status === "Draft" ? "profile-status--draft" : "profile-status--disabled"
        }`}
      >
        {profile.status}
      </span>
    ),
  },
  {
    title: "Schedule",
    label: "startTime",
    renderProps: (profile: TimerProfile) =>
      // `${profile.startTime} - ${profile.endTime} • ${profile.dayOfWeek}`,
      `${profile.startTime} • ${profile.dayOfWeek}`,
  },
  {
    title: "Zone",
    label: "zoneId",
    renderProps: (profile: TimerProfile) =>
      profile.group || String(profile.zoneId) || "--",
  },
];

export function TimerProfileTable({
  assetCode,
  selectedProfileId,
  commandStatus,
  commandCountdown,
  isLoadingCommandResult,
  onSelectProfile,
  onGetProfileConfig,
  onViewCommandResult,
  onAddProfile,
  onTableDataReady,
}: TimerProfileTableProps) {
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState("ProfileName");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const { data: profilesResponse, isLoading, refetch: refetchProfiles } = useQuery({
    queryKey: ["getTimerProfiles", assetCode, pageNumber, sortBy, sortDirection],
    queryFn: ({ signal }) =>
      getTimerProfiles(
        assetCode || "",
        { pageNumber, pageSize: 10, sortBy, sortDirection },
        signal
      ),
    enabled: !!assetCode,
    retry: false,
  });

  const profiles = useMemo<TimerProfile[]>(() => profilesResponse?.data ?? [], [profilesResponse?.data]);

  const onTableDataReadyRef = useRef(onTableDataReady);
  onTableDataReadyRef.current = onTableDataReady;

  useEffect(() => {
    if (onTableDataReadyRef.current) {
      onTableDataReadyRef.current({
        refetch: async () => {
          await refetchProfiles();
        },
        getProfiles: () => profiles,
      });
    }
  }, [refetchProfiles, profiles]);
  const totalRecords = profilesResponse?.numberOfRecords ?? profiles.length;
  const activeCount = profiles.filter((p) => p.status === "Enabled").length;

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleSortChange = (field: string, direction: string) => {
    if (!field) return;
    setSortBy(field);
    setSortDirection(direction.toUpperCase() as "ASC" | "DESC");
    setPageNumber(1);
  };

  return (
    <section className="configuration-card configuration-card--table">
      <div className="configuration-card__header configuration-card__header--middle">
        <div className="profile-table-card__header-left">
          <span className="profile-table-card__icon">
            <FontAwesomeIcon icon={faClock} />
          </span>
          <h3 className="profile-table-card__title">Timer Profiles</h3>
          <span className="profile-table-card__badge">{activeCount} Active</span>
        </div>
        <div className="configuration-card__header-actions">
          <GetConfigButton
            status={commandStatus}
            isLoading={isLoadingCommandResult}
            onGetConfig={onGetProfileConfig}
            onViewResult={onViewCommandResult}
            label="Get Profile Config"
            countdown={commandCountdown}
          />
          <button
            type="button"
            className="configuration-button configuration-button--primary configuration-button--compact"
            onClick={onAddProfile}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Timer Profile
          </button>
        </div>
      </div>

      <NewTableComponent
        tableHeaders={tableHeaders}
        data={profiles}
        pageSize={10}
        current={pageNumber}
        totalNumberOfRecords={totalRecords}
        onChangePage={handlePageChange}
        onChangeSort={handleSortChange}
        isLoadingData={isLoading}
        emptyConfig={{
          title: "No Profiles Found",
          description: 'Click "Add Timer Profile" to create one.',
        }}
        wrapperHeight="400px"
        count={6}
        onRowClick={onSelectProfile}
        selectedRowId={selectedProfileId}
        rowIdKey="profileId"
      />
    </section>
  );
}
